import * as Sentry from "@sentry/react";
import clarity from "@microsoft/clarity";
import { createRoot } from "react-dom/client";
import { scrubSentryBreadcrumb, scrubSentryEvent, scrubSentryLog } from "@shared/sentryScrub";
import App from "./App";
import "./index.css";
import "./claimhive-tokens.css";

// Initialize Sentry before rendering the app. Configured from build-time env only;
// with no VITE_SENTRY_DSN it stays off.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const isSentryEnabled = sentryDsn && sentryDsn.startsWith('https://');
if (isSentryEnabled) {
  // Without VITE_SENTRY_RELEASE, the release injected by the Sentry Vite plugin (if it ran) is used.
  const release = import.meta.env.VITE_SENTRY_RELEASE;
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.VITE_APP_ENV || import.meta.env.MODE,
    ...(release ? { release } : {}),
    sendDefaultPii: import.meta.env.VITE_SENTRY_SEND_DEFAULT_PII === "true",
    enableLogs: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Recordings must never capture claim data (names, addresses, photos).
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
      Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
    ],
    beforeSend(event) {
      // Stale chunk errors after deploys are handled by lazyWithRetry (page reload).
      // Drop them from Sentry to avoid noise.
      const msg = event.exception?.values?.[0]?.value ?? "";
      if (
        msg.includes("Failed to fetch dynamically imported module") ||
        msg.includes("Importing a module script failed") ||
        msg.includes("error loading dynamically imported module")
      ) {
        return null;
      }
      // Drop stale-bundle errors from old super-admin code executed in cached browsers.
      if (msg.includes("isSuperAdmin")) {
        return null;
      }
      // Personal and claim data is redacted before anything leaves the browser.
      return scrubSentryEvent(event);
    },
    beforeSendTransaction: scrubSentryEvent,
    beforeBreadcrumb: scrubSentryBreadcrumb,
    beforeSendLog: scrubSentryLog,
    tracesSampleRate: import.meta.env.VITE_APP_ENV === "production" ? 0.2 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Clarity stays on through beta, but it must never record claim data. <body> in
// client/index.html carries data-clarity-mask="true", so all content (including
// portaled dialogs and toasts) is masked by default. Set the Clarity project's
// masking mode to Strict as well (Settings > Masking).
clarity.init("yg6jpo08ft");

createRoot(document.getElementById("root")!, {
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.error("Uncaught error:", error, errorInfo);
  }),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(<App />);
